import { useState, useContext, useRef } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import './Login.css';

const MAX_ATTEMPTS = 5;
const LOCKOUT_MS = 60 * 1000;

export default function Login() {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const captchaRef = useRef(null);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [token, setToken] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const attemptsRef = useRef(0);
  const lockedUntilRef = useRef(null);
  const [lockedSecondsLeft, setLockedSecondsLeft] = useState(0);

  const startLockoutCountdown = (until) => {
    const tick = () => {
      const remaining = Math.ceil((until - Date.now()) / 1000);
      if (remaining <= 0) {
        setLockedSecondsLeft(0);
        attemptsRef.current = 0;
        lockedUntilRef.current = null;
        setError(null);
      } else {
        setLockedSecondsLeft(remaining);
        setTimeout(tick, 1000);
      }
    };
    tick();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (lockedUntilRef.current && Date.now() < lockedUntilRef.current) {
      const remaining = Math.ceil((lockedUntilRef.current - Date.now()) / 1000);
      setError(`Too many failed attempts. Try again in ${remaining}s.`);
      return;
    }

    if (!username.trim()) {
      setError('Username is required.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (!token) {
      setError('Please complete the captcha.');
      return;
    }

    setLoading(true);
    try {
      await login(username.trim(), password);
      navigate('/');
    } catch (err) {
      attemptsRef.current += 1;
      const remaining = MAX_ATTEMPTS - attemptsRef.current;

      if (attemptsRef.current >= MAX_ATTEMPTS) {
        const until = Date.now() + LOCKOUT_MS;
        lockedUntilRef.current = until;
        startLockoutCountdown(until);
        setError('Too many failed attempts. Locked out for 60 seconds.');
      } else {
        setError(`Invalid username or password. ${remaining} attempt${remaining !== 1 ? 's' : ''} remaining.`);
      }

      setToken(null);
      captchaRef.current?.resetCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const isLocked = !!(lockedUntilRef.current && Date.now() < lockedUntilRef.current);
  const isDisabled = loading || isLocked;

  return (
    <div className="login-page">
      <div className="login-card">

        <div className="login-icon-wrap">
          <i className="bi bi-shield-lock-fill"></i>
        </div>
        <h4 className="login-title">Business Dashboard</h4>
        <p className="login-subtitle">Sign in to your account</p>

        <form onSubmit={handleSubmit} noValidate>

          <div className="mb-3">
            <label className="login-label" htmlFor="login-username">Username</label>
            <div className="login-input-group">
              <span className="login-input-prefix">
                <i className="bi bi-person-fill"></i>
              </span>
              <input
                id="login-username"
                type="text"
                className="login-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username"
                autoComplete="username"
                disabled={isDisabled}
                required
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="login-label" htmlFor="login-password">Password</label>
            <div className="login-input-group">
              <span className="login-input-prefix">
                <i className="bi bi-lock-fill"></i>
              </span>
              <input
                id="login-password"
                type={showPassword ? 'text' : 'password'}
                className="login-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                autoComplete="current-password"
                disabled={isDisabled}
                minLength={6}
                required
              />
              <button
                type="button"
                className="login-eye-btn"
                tabIndex={-1}
                onClick={() => setShowPassword(v => !v)}
                disabled={isDisabled}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
              </button>
            </div>
          </div>

          {error && (
            <div className={`login-alert ${isLocked ? 'lockout' : 'warning'}`}>
              <i className={`bi ${isLocked ? 'bi-shield-x' : 'bi-exclamation-triangle'} mt-1`}></i>
              <span>{error}</span>
            </div>
          )}

          {isLocked && lockedSecondsLeft > 0 && (
            <p className="login-countdown">
              Unlocks in <span>{lockedSecondsLeft}s</span>
            </p>
          )}

          <div className="login-captcha">
            <HCaptcha
              sitekey="54cac01c-23b0-4322-88c8-d302356e1c02"
              ref={captchaRef}
              onVerify={setToken}
              onExpire={() => setToken(null)}
              onError={() => setToken(null)}
            />
          </div>

          <button
            type="submit"
            className="login-submit-btn"
            disabled={isDisabled}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" />
                Signing in…
              </>
            ) : (
              <>
                <i className="bi bi-box-arrow-in-right"></i>
                Sign In
              </>
            )}
          </button>

        </form>

        <p className="login-footer">
          <i className="bi bi-lock me-1"></i>
          Secured connection
        </p>
      </div>
    </div>
  );
}
